import React, { useRef, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

// Palette de couleurs standard (comme Word)
const COLORS = [
  { name: "Noir", value: "#000000" },
  { name: "Gris foncé", value: "#444444" },
  { name: "Gris", value: "#666666" },
  { name: "Gris clair", value: "#999999" },
  { name: "Argent", value: "#CCCCCC" },
  { name: "Blanc", value: "#FFFFFF" },
  { name: "Rouge", value: "#FF0000" },
  { name: "Orange", value: "#FF9900" },
  { name: "Jaune", value: "#FFFF00" },
  { name: "Vert", value: "#00FF00" },
  { name: "Cyan", value: "#00FFFF" },
  { name: "Bleu", value: "#0000FF" },
  { name: "Violet", value: "#9900FF" },
  { name: "Magenta", value: "#FF00FF" },
];

const RichTextEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const [selectedFont, setSelectedFont] = useState('');
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [selectedSize, setSelectedSize] = useState('normal');
  const [showColorPalette, setShowColorPalette] = useState(false);

  // Initialiser le contenu une seule fois
  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, []);

  // Sauvegarder la position du curseur
  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      return selection.getRangeAt(0);
    }
    return null;
  };

  // Restaurer la position du curseur
  const restoreCursorPosition = (range) => {
    if (range) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  // Gérer les changements
  const handleInput = (e) => {
    const content = e.currentTarget.innerHTML;
    onChange(content);
  };

  // Gérer Enter pour forcer un saut de ligne propre
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      // Insérer un vrai saut de ligne
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      
      const br = document.createElement('br');
      range.deleteContents();
      range.insertNode(br);
      
      // Ajouter un espace après pour éviter que le curseur se bloque
      const space = document.createTextNode('\u200B'); // Zero-width space
      range.insertNode(space);
      
      // Déplacer le curseur après le br
      range.setStartAfter(br);
      range.setEndAfter(br);
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Déclencher l'événement de changement
      handleInput(e);
    }
  };

  // Commandes de formatage
  const execCommand = (command, value = null) => {
    // S'assurer que l'éditeur a le focus
    if (editorRef.current) {
      editorRef.current.focus();
    }
    
    // Exécuter la commande
    document.execCommand(command, false, value);
    
    // Déclencher l'événement de changement
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // Appliquer la police
  const applyFont = (font) => {
    setSelectedFont(font);
    execCommand('fontName', font);
  };

  // Appliquer la couleur depuis la palette
  const applyColor = (color) => {
    setSelectedColor(color);
    setShowColorPalette(false);
    
    if (editorRef.current) {
      editorRef.current.focus();
    }
    
    execCommand('foreColor', color);
  };

  // ========== CORRECTION DÉFINITIVE EXPOSANT/INDICE ==========
  // Toggle basé sur manipulation DOM directe (ne dépend plus de execCommand)
  const toggleSupSub = (type) => {
    if (!editorRef.current) return;
    
    editorRef.current.focus();
    
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const parent = range.commonAncestorContainer.parentElement;

    const tag = type === 'sup' ? 'SUP' : 'SUB';

    // Si déjà dans un <sup> ou <sub> → supprimer proprement
    if (parent && parent.tagName === tag) {
      const textNode = document.createTextNode(parent.textContent);
      parent.replaceWith(textNode);
      
      // Déclencher changement
      onChange(editorRef.current.innerHTML);
      return;
    }

    // Sinon → appliquer le tag
    try {
      const selectedContent = range.extractContents();
      const wrapper = document.createElement(tag);
      wrapper.appendChild(selectedContent);
      range.insertNode(wrapper);
      
      // Replacer le curseur après l'élément inséré
      range.setStartAfter(wrapper);
      range.setEndAfter(wrapper);
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Déclencher changement
      onChange(editorRef.current.innerHTML);
    } catch (e) {
      console.error('Erreur toggle sup/sub:', e);
    }
  };

  // ========== CORRECTION DÉFINITIVE LISTES ==========
  // Création manuelle DOM (ne dépend plus de execCommand)
  const createList = (type) => {
    if (!editorRef.current) return;
    
    editorRef.current.focus();
    
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    
    // Vérifier si on est déjà dans une liste
    let node = range.commonAncestorContainer;
    while (node && node !== editorRef.current) {
      if (node.nodeName === 'UL' || node.nodeName === 'OL') {
        // Déjà dans une liste → la supprimer
        removeList();
        return;
      }
      node = node.parentNode;
    }

    // Créer la liste
    const selectedText = range.toString();

    if (!selectedText) {
      // Pas de sélection → créer une liste avec un élément vide
      const tag = type === 'ul' ? 'ul' : 'ol';
      const list = document.createElement(tag);
      const li = document.createElement('li');
      li.textContent = 'Nouvel élément';
      list.appendChild(li);
      
      range.deleteContents();
      range.insertNode(list);
      
      // Placer le curseur dans le <li>
      range.selectNodeContents(li);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // Sélection présente → créer liste avec le texte sélectionné
      const tag = type === 'ul' ? 'ul' : 'ol';
      const list = document.createElement(tag);

      // Séparer par lignes si multi-lignes
      const lines = selectedText.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) {
        lines.push('Nouvel élément');
      }

      lines.forEach(line => {
        const li = document.createElement('li');
        li.textContent = line.trim();
        list.appendChild(li);
      });

      range.deleteContents();
      range.insertNode(list);
      
      // Replacer le curseur après la liste
      range.setStartAfter(list);
      range.setEndAfter(list);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    // Déclencher changement
    onChange(editorRef.current.innerHTML);
  };

  // Suppression liste (toggle inverse)
  const removeList = () => {
    if (!editorRef.current) return;
    
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    let node = selection.anchorNode;

    // Remonter jusqu'à trouver UL ou OL
    while (node && node !== editorRef.current) {
      if (node.nodeName === 'UL' || node.nodeName === 'OL') {
        break;
      }
      node = node.parentNode;
    }

    if (node && (node.nodeName === 'UL' || node.nodeName === 'OL')) {
      const fragment = document.createDocumentFragment();

      // Récupérer le texte de chaque <li>
      Array.from(node.children).forEach(li => {
        const textNode = document.createTextNode(li.textContent);
        fragment.appendChild(textNode);
        fragment.appendChild(document.createElement('br'));
      });

      // Remplacer la liste par le fragment
      node.replaceWith(fragment);

      // Déclencher changement
      onChange(editorRef.current.innerHTML);
    }
  };

  // Appliquer la taille
  const applySize = (size) => {
    setSelectedSize(size);
    
    // Mapping des tailles
    const sizeMap = {
      'petit': '14px',
      'normal': '16px',
      'grand': '20px',
      'tres-grand': '24px'
    };
    
    const fontSize = sizeMap[size];
    
    // Workaround pour fontSize avec execCommand
    // On utilise fontSize 7 temporairement puis on remplace
    execCommand('fontSize', '7');
    
    // Remplacer les <font size="7"> par <span style="font-size: ...">
    setTimeout(() => {
      if (editorRef.current) {
        const content = editorRef.current.innerHTML;
        const replaced = content.replace(
          /<font size="7">(.*?)<\/font>/gi,
          `<span style="font-size: ${fontSize}">$1</span>`
        );
        editorRef.current.innerHTML = replaced;
        onChange(replaced);
      }
    }, 10);
  };

  return (
    <div className="space-y-2">
      {/* Barre d'outils */}
      <div className="space-y-2">
        {/* Section 1: Typographie avancée */}
        <div className="flex flex-wrap gap-2 p-2 bg-gray-100 rounded border">
          {/* Choix de police */}
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-600 whitespace-nowrap">Police:</label>
            <Select value={selectedFont} onValueChange={applyFont}>
              <SelectTrigger className="h-8 w-[140px] text-xs">
                <SelectValue placeholder="Défaut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Arial">Arial</SelectItem>
                <SelectItem value="Helvetica">Helvetica</SelectItem>
                <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                <SelectItem value="Georgia">Georgia</SelectItem>
                <SelectItem value="Verdana">Verdana</SelectItem>
                <SelectItem value="Roboto">Roboto</SelectItem>
                <SelectItem value="Open Sans">Open Sans</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Taille de police */}
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-600 whitespace-nowrap">Taille:</label>
            <Select value={selectedSize} onValueChange={applySize}>
              <SelectTrigger className="h-8 w-[110px] text-xs">
                <SelectValue placeholder="Normal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="petit">Petit</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="grand">Grand</SelectItem>
                <SelectItem value="tres-grand">Très grand</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Palette de couleurs */}
          <div className="flex items-center gap-1 relative">
            <label className="text-xs text-gray-600 whitespace-nowrap">Couleur:</label>
            <div className="flex gap-1">
              {/* Preview couleur sélectionnée */}
              <button
                type="button"
                onClick={() => setShowColorPalette(!showColorPalette)}
                className="h-8 w-8 rounded border-2 border-gray-300 cursor-pointer hover:border-gray-400 transition-colors"
                style={{ backgroundColor: selectedColor }}
                title={`Couleur actuelle: ${selectedColor}`}
                data-testid="color-preview-button"
              />
              
              {/* Color picker custom optionnel */}
              <input
                type="color"
                value={selectedColor}
                onChange={(e) => applyColor(e.target.value)}
                onMouseDown={(e) => e.preventDefault()}
                className="h-8 w-8 cursor-pointer border rounded"
                title="Couleur personnalisée"
                data-testid="custom-color-picker"
              />
            </div>
            
            {/* Palette déroulante */}
            {showColorPalette && (
              <div 
                className="absolute top-10 left-0 z-50 bg-white border rounded-lg shadow-lg p-3"
                data-testid="color-palette"
              >
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => applyColor(color.value)}
                      className="w-8 h-8 rounded border-2 border-gray-300 hover:border-gray-500 transition-all hover:scale-110 cursor-pointer"
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                      data-testid={`color-${color.value}`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setShowColorPalette(false)}
                  className="w-full text-xs text-gray-600 hover:text-gray-800 py-1"
                >
                  Fermer
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Section 2: Formatage de base */}
        <div className="flex flex-wrap gap-1 p-2 bg-gray-100 rounded border">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2"
            onMouseDown={(e) => {
              e.preventDefault(); // Évite que l'éditeur perde le focus
              execCommand('bold');
            }}
            title="Gras (Ctrl+B)"
            data-testid="bold-button"
          >
            <strong>B</strong>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2"
            onMouseDown={(e) => {
              e.preventDefault();
              execCommand('italic');
            }}
            title="Italique (Ctrl+I)"
            data-testid="italic-button"
          >
            <em>I</em>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2"
            onMouseDown={(e) => {
              e.preventDefault();
              execCommand('underline');
            }}
            title="Souligné (Ctrl+U)"
            data-testid="underline-button"
          >
            <u>U</u>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2"
            onMouseDown={(e) => {
              e.preventDefault();
              execCommand('strikeThrough');
            }}
            title="Barré"
            data-testid="strikethrough-button"
          >
            <s>S</s>
          </Button>
          
          <div className="w-px h-8 bg-gray-300 mx-1"></div>
          
          {/* Exposant et indice avec TOGGLE DOM */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2 text-xs min-w-[44px]"
            onMouseDown={(e) => {
              e.preventDefault();
              toggleSupSub('sup'); // ← Nouvelle fonction DOM
            }}
            title="Exposant (toggle ON/OFF)"
            data-testid="superscript-button"
          >
            X<sup>2</sup>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2 text-xs min-w-[44px]"
            onMouseDown={(e) => {
              e.preventDefault();
              toggleSupSub('sub'); // ← Nouvelle fonction DOM
            }}
            title="Indice (toggle ON/OFF)"
            data-testid="subscript-button"
          >
            H<sub>2</sub>O
          </Button>
          
          <div className="w-px h-8 bg-gray-300 mx-1"></div>
          
          {/* Listes avec création DOM manuelle */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2 text-xs min-w-[44px]"
            onMouseDown={(e) => {
              e.preventDefault();
              createList('ul'); // ← Nouvelle fonction DOM
            }}
            title="Liste à puces (toggle)"
            data-testid="bullet-list-button"
          >
            • Liste
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2 text-xs min-w-[44px]"
            onMouseDown={(e) => {
              e.preventDefault();
              createList('ol'); // ← Nouvelle fonction DOM
            }}
            title="Liste numérotée (toggle)"
            data-testid="numbered-list-button"
          >
            1. Liste
          </Button>
          
          <div className="w-px h-8 bg-gray-300 mx-1"></div>
          
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2"
            onMouseDown={(e) => {
              e.preventDefault();
              execCommand('justifyLeft');
            }}
            title="Aligner à gauche"
            data-testid="align-left-button"
          >
            ←
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2"
            onMouseDown={(e) => {
              e.preventDefault();
              execCommand('justifyCenter');
            }}
            title="Centrer"
            data-testid="align-center-button"
          >
            ↔
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2"
            onMouseDown={(e) => {
              e.preventDefault();
              execCommand('justifyRight');
            }}
            title="Aligner à droite"
            data-testid="align-right-button"
          >
            →
          </Button>
        </div>
      </div>

      {/* Zone d'édition */}
      <div
        ref={editorRef}
        contentEditable
        className="min-h-[200px] p-4 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary overflow-auto rich-text-content"
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        suppressContentEditableWarning
        style={{
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word'
        }}
      />
      
      <p className="text-xs text-muted-foreground">
        💡 <strong>Exposant/Indice:</strong> Cliquez une fois pour activer, deux fois pour désactiver. 
        <strong>Listes:</strong> Sélectionnez du texte puis cliquez.
      </p>
    </div>
  );
};

export default RichTextEditor;
